/**
 * TeklifEditor — Teklif Modülü v2
 *
 * Yeni teklif oluşturma / düzenleme.
 * - Taahhüt: Müşteri, proje, WBS kalemleri
 * - Tedarikçi: Firma adı, verilen tarih, geçerlilik, ürün/hizmet listesi
 *
 * URL querystring: /teklifler/yeni?type=taahhut|tedarikci
 */
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { API, formatErr } from "@/context/AuthContext";
import Shell from "@/components/Shell";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  FloppyDisk, ArrowLeft, Plus, TrashSimple, FilePdf, MicrosoftExcelLogo,
  Buildings, Package,
} from "@phosphor-icons/react";

const STATUS_OPTIONS = [
  { value: "taslak", label: "Taslak" },
  { value: "gonderildi", label: "Gönderildi" },
  { value: "onaylandi", label: "Onaylandı" },
  { value: "reddedildi", label: "Reddedildi" },
];
const CURRENCY_OPTIONS = [
  { value: "TRY", label: "TL (Türk Lirası)" },
  { value: "USD", label: "USD (Dolar)" },
  { value: "EUR", label: "EUR (Euro)" },
];

const TYPE_META = {
  taahhut: {
    label: "Taahhüt Teklifi",
    Icon: Buildings,
    accent: "indigo",
    poz_label: "Poz. Kodu / WBS",
    desc_label: "İş Kalemi Açıklaması",
    cat_placeholder: "Hafriyat, Betonarme, İnce İş…",
  },
  tedarikci: {
    label: "Tedarikçi Teklifi",
    Icon: Package,
    accent: "amber",
    poz_label: "Ürün Kodu",
    desc_label: "Ürün / Hizmet Açıklaması",
    cat_placeholder: "Malzeme, Nakliye, Hizmet…",
  },
};

const emptyItem = { position_code: "", description: "", unit: "", quantity: 0, unit_price: 0, category: "" };
const makeEmpty = (type) => ({
  type,
  customer: { name: "", contact_person: "", phone: "", email: "", address: "" },
  supplier_name: "",
  project_name: "",
  work_location: "",
  teklif_date: new Date().toISOString().slice(0, 10),
  validity_days: 30,
  currency: "TRY",
  kdv_rate: 20,
  notes: "",
  status: "taslak",
  items: [{ ...emptyItem }],
});

const fmtMoney = (v) => Number(v || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const curSym = (c) => ({ TRY: "TL", USD: "USD", EUR: "EUR" }[c] || "TL");

export default function TeklifEditor() {
  const nav = useNavigate();
  const { id } = useParams();
  const [sp] = useSearchParams();
  const isNew = !id;
  const initialType = (sp.get("type") === "tedarikci") ? "tedarikci" : "taahhut";

  const [form, setForm] = useState(() => makeEmpty(initialType));
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [number, setNumber] = useState("");

  useEffect(() => {
    if (!id) return;
    (async () => {
      setLoading(true);
      try {
        const r = await axios.get(`${API}/teklifler/${id}`);
        const base = makeEmpty(r.data.type || "taahhut");
        setForm({
          ...base,
          ...r.data,
          customer: { ...base.customer, ...(r.data.customer || {}) },
          items: (r.data.items || []).length ? r.data.items : [{ ...emptyItem }],
        });
        setNumber(r.data.number || "");
      } catch (e) { toast.error(formatErr(e)); }
      finally { setLoading(false); }
    })();
  }, [id]);

  const type = form.type || "taahhut";
  const meta = TYPE_META[type];

  // Totals
  const totals = useMemo(() => {
    const sub = (form.items || []).reduce(
      (acc, it) => acc + (Number(it.quantity || 0) * Number(it.unit_price || 0)),
      0,
    );
    const kdv = sub * (Number(form.kdv_rate || 0) / 100);
    return { subtotal: sub, kdv_amount: kdv, grand_total: sub + kdv };
  }, [form.items, form.kdv_rate]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setCustomer = (k, v) => setForm((f) => ({ ...f, customer: { ...f.customer, [k]: v } }));
  const setItem = (idx, k, v) => setForm((f) => ({
    ...f,
    items: f.items.map((it, i) => i === idx ? { ...it, [k]: v } : it),
  }));
  const addItem = () => setForm((f) => ({ ...f, items: [...f.items, { ...emptyItem }] }));
  const removeItem = (idx) => setForm((f) => ({
    ...f,
    items: f.items.filter((_, i) => i !== idx),
  }));

  const save = async () => {
    if (type === "taahhut" && !form.customer?.name?.trim()) {
      toast.error("Müşteri adı zorunludur");
      return;
    }
    if (type === "tedarikci" && !form.supplier_name?.trim()) {
      toast.error("Tedarikçi firma adı zorunludur");
      return;
    }
    if (!form.items?.length) { toast.error("En az bir kalem olmalı"); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        type,
        validity_days: Number(form.validity_days || 30),
        kdv_rate: Number(form.kdv_rate || 0),
        items: form.items.map((it, i) => ({
          ...it,
          order: i + 1,
          quantity: Number(it.quantity || 0),
          unit_price: Number(it.unit_price || 0),
        })),
      };
      let r;
      if (isNew) {
        r = await axios.post(`${API}/teklifler`, payload);
        toast.success(`Teklif oluşturuldu: ${r.data?.number}`);
        nav(`/teklifler/${r.data.id}`, { replace: true });
      } else {
        r = await axios.put(`${API}/teklifler/${id}`, payload);
        toast.success("Teklif kaydedildi");
      }
    } catch (e) { toast.error(formatErr(e)); }
    finally { setSaving(false); }
  };

  const downloadPdf = async () => {
    if (isNew) return;
    try {
      const r = await axios.get(`${API}/teklifler/${id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) { toast.error(formatErr(e)); }
  };
  const downloadExcel = async () => {
    if (isNew) return;
    try {
      const r = await axios.get(`${API}/teklifler/${id}/excel`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a"); a.href = url;
      a.download = `${number || "teklif"}.xlsx`; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) { toast.error(formatErr(e)); }
  };

  if (loading) return <Shell><div className="p-10 text-center text-slate-400">Yükleniyor…</div></Shell>;

  const Icon = meta.Icon;

  return (
    <Shell>
      <div className="max-w-[1200px] mx-auto px-4 py-6 space-y-5" data-testid="teklif-editor-page">
        {/* Header */}
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Button variant="ghost" onClick={() => nav("/teklifler")} data-testid="btn-back">
              <ArrowLeft size={16} className="mr-1.5" /> Geri
            </Button>
            <div className="flex items-center gap-2">
              <Icon size={22} weight="duotone" className="text-[#1B4776]" />
              <h1 className="text-xl font-bold text-slate-900" data-testid="editor-title">
                {isNew ? `Yeni ${meta.label}` : `${meta.label} ${number}`}
              </h1>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {!isNew && (
              <>
                <Button variant="outline" size="sm" onClick={downloadPdf} data-testid="btn-dl-pdf">
                  <FilePdf size={14} className="mr-1 text-rose-600" /> PDF
                </Button>
                <Button variant="outline" size="sm" onClick={downloadExcel} data-testid="btn-dl-excel">
                  <MicrosoftExcelLogo size={14} className="mr-1 text-emerald-700" /> Excel
                </Button>
              </>
            )}
            <Button
              onClick={save}
              disabled={saving}
              className="bg-[#1B4776] hover:bg-[#163a63] text-white"
              data-testid="btn-save"
            >
              <FloppyDisk size={16} className="mr-1.5" />
              {saving ? "Kaydediliyor…" : "Kaydet"}
            </Button>
          </div>
        </div>

        {/* TAAHHUT: Müşteri + Proje bilgisi */}
        {type === "taahhut" && (
          <Card className="p-5" data-testid="party-card-taahhut">
            <h2 className="text-sm font-bold text-slate-700 uppercase tracking-wider mb-3">Müşteri &amp; Proje</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Müşteri Adı <span className="text-rose-500">*</span></Label>
                <Input value={form.customer?.name || ""} onChange={(e) => setCustomer("name", e.target.value)} data-testid="cust-name" />
              </div>
              <div>
                <Label>İlgili Kişi</Label>
                <Input value={form.customer?.contact_person || ""} onChange={(e) => setCustomer("contact_person", e.target.value)} />
              </div>
              <div>
                <Label>Telefon</Label>
                <Input value={form.customer?.phone || ""} onChange={(e) => setCustomer("phone", e.target.value)} />
              </div>
              <div>
                <Label>E-posta</Label>
                <Input type="email" value={form.customer?.email || ""} onChange={(e) => setCustomer("email", e.target.value)} />
              </div>
              <div>
                <Label>Proje Adı</Label>
                <Input value={form.project_name || ""} onChange={(e) => setField("project_name", e.target.value)} />
              </div>
              <div>
                <Label>İş Yeri / Lokasyon</Label>
                <Input value={form.work_location || ""} onChange={(e) => setField("work_location", e.target.value)} />
              </div>
            </div>
          </Card>
        )}

        {/* TEDARIKCI: Firma bilgisi */}
        {type === "tedarikci" && (
          <Card className="p-5" data-testid="party-card-tedarikci">
            <h2 className="text-sm font-bold text-slate-700 uppercase tracking-wider mb-3">Tedarikçi Firma</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <Label>Firma Adı <span className="text-rose-500">*</span></Label>
                <Input
                  value={form.supplier_name || ""}
                  onChange={(e) => setField("supplier_name", e.target.value)}
                  data-testid="supplier-name"
                  placeholder="Örn. ABC İnşaat Malzemeleri Ltd. Şti."
                />
              </div>
              <div>
                <Label>İlgili Not / Açıklama</Label>
                <Input
                  value={form.project_name || ""}
                  onChange={(e) => setField("project_name", e.target.value)}
                  placeholder="Örn. Nisan dönemi malzeme teklifi"
                />
              </div>
              <div>
                <Label>İş Yeri / Teslim Yeri</Label>
                <Input value={form.work_location || ""} onChange={(e) => setField("work_location", e.target.value)} />
              </div>
            </div>
          </Card>
        )}

        {/* Teklif ayarları */}
        <Card className="p-5">
          <h2 className="text-sm font-bold text-slate-700 uppercase tracking-wider mb-3">Teklif Ayarları</h2>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div>
              <Label>{type === "tedarikci" ? "Verilen Tarih" : "Teklif Tarihi"}</Label>
              <Input type="date" value={form.teklif_date || ""} onChange={(e) => setField("teklif_date", e.target.value)} data-testid="teklif-date" />
            </div>
            <div>
              <Label>Geçerlilik (gün)</Label>
              <Input type="number" value={form.validity_days} onChange={(e) => setField("validity_days", e.target.value)} data-testid="validity-days" />
            </div>
            <div>
              <Label>Para Birimi</Label>
              <Select value={form.currency} onValueChange={(v) => setField("currency", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CURRENCY_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>KDV Oranı (%)</Label>
              <Input type="number" value={form.kdv_rate} onChange={(e) => setField("kdv_rate", e.target.value)} />
            </div>
            <div>
              <Label>Durum</Label>
              <Select value={form.status} onValueChange={(v) => setField("status", v)}>
                <SelectTrigger data-testid="status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
        </Card>

        {/* Kalemler */}
        <Card className="p-5">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-bold text-slate-700 uppercase tracking-wider">
              {type === "tedarikci" ? "Ürün / Hizmet Kalemleri" : "Teklif Kalemleri"}
            </h2>
            <Button size="sm" variant="outline" onClick={addItem} data-testid="btn-add-item">
              <Plus size={14} className="mr-1" /> Kalem Ekle
            </Button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="items-table">
              <thead className="bg-slate-50 border-y border-slate-200">
                <tr className="text-xs">
                  <th className="text-center px-2 py-2 w-12 font-semibold">#</th>
                  <th className="text-left px-2 py-2 w-28 font-semibold">{meta.poz_label}</th>
                  <th className="text-left px-2 py-2 w-32 font-semibold">Kategori</th>
                  <th className="text-left px-2 py-2 font-semibold">{meta.desc_label}</th>
                  <th className="text-left px-2 py-2 w-20 font-semibold">Birim</th>
                  <th className="text-right px-2 py-2 w-24 font-semibold">
                    {type === "tedarikci" ? "Adet" : "Miktar"}
                  </th>
                  <th className="text-right px-2 py-2 w-28 font-semibold">Birim Fiyat</th>
                  <th className="text-right px-2 py-2 w-32 font-semibold">Toplam</th>
                  <th className="text-center px-2 py-2 w-10"></th>
                </tr>
              </thead>
              <tbody>
                {form.items.map((it, i) => {
                  const total = Number(it.quantity || 0) * Number(it.unit_price || 0);
                  return (
                    <tr key={i} className="border-b border-slate-100" data-testid={`item-row-${i}`}>
                      <td className="text-center px-2 py-1.5 text-slate-500 text-xs">{i + 1}</td>
                      <td className="px-1 py-1.5">
                        <Input className="h-8 text-xs" value={it.position_code} onChange={(e) => setItem(i, "position_code", e.target.value)} data-testid={`item-poz-${i}`} />
                      </td>
                      <td className="px-1 py-1.5">
                        <Input className="h-8 text-xs" placeholder={meta.cat_placeholder} value={it.category} onChange={(e) => setItem(i, "category", e.target.value)} />
                      </td>
                      <td className="px-1 py-1.5">
                        <Input className="h-8 text-xs" value={it.description} onChange={(e) => setItem(i, "description", e.target.value)} data-testid={`item-desc-${i}`} />
                      </td>
                      <td className="px-1 py-1.5">
                        <Input className="h-8 text-xs" value={it.unit} onChange={(e) => setItem(i, "unit", e.target.value)} placeholder={type === "tedarikci" ? "ad, kg, m…" : "m², m³…"} />
                      </td>
                      <td className="px-1 py-1.5">
                        <Input type="number" step="0.01" className="h-8 text-xs text-right" value={it.quantity} onChange={(e) => setItem(i, "quantity", e.target.value)} data-testid={`item-qty-${i}`} />
                      </td>
                      <td className="px-1 py-1.5">
                        <Input type="number" step="0.01" className="h-8 text-xs text-right" value={it.unit_price} onChange={(e) => setItem(i, "unit_price", e.target.value)} data-testid={`item-price-${i}`} />
                      </td>
                      <td className="text-right px-2 py-1.5 font-mono text-xs font-semibold text-slate-900">
                        {fmtMoney(total)}
                      </td>
                      <td className="text-center px-1 py-1.5">
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-rose-600 hover:bg-rose-50" onClick={() => removeItem(i)}>
                          <TrashSimple size={13} />
                        </Button>
                      </td>
                    </tr>
                  );
                })}
                {form.items.length === 0 && (
                  <tr><td colSpan={9} className="text-center py-6 text-slate-400 text-xs italic">Henüz kalem yok</td></tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Totals */}
          <div className="flex justify-end mt-4">
            <div className="w-80 space-y-1 text-sm">
              <div className="flex justify-between py-1">
                <span className="text-slate-600">Ara Toplam:</span>
                <span className="font-mono font-semibold" data-testid="total-subtotal">{fmtMoney(totals.subtotal)} {curSym(form.currency)}</span>
              </div>
              <div className="flex justify-between py-1">
                <span className="text-slate-600">KDV (%{form.kdv_rate}):</span>
                <span className="font-mono font-semibold">{fmtMoney(totals.kdv_amount)} {curSym(form.currency)}</span>
              </div>
              <div className="flex justify-between py-2 border-t-2 border-[#1B4776]">
                <span className="font-bold text-[#1B4776]">GENEL TOPLAM:</span>
                <span className="font-mono font-bold text-[#1B4776] text-base" data-testid="total-grand">
                  {fmtMoney(totals.grand_total)} {curSym(form.currency)}
                </span>
              </div>
            </div>
          </div>
        </Card>

        {/* Notlar */}
        <Card className="p-5">
          <Label className="text-sm font-bold text-slate-700 uppercase tracking-wider">Özel Şartlar / Notlar</Label>
          <Textarea
            className="mt-2"
            rows={4}
            value={form.notes || ""}
            onChange={(e) => setField("notes", e.target.value)}
            placeholder={type === "tedarikci"
              ? "Ödeme vadesi, teslim süresi, nakliye koşulları…"
              : "Ödeme planı, teslim süresi, garanti şartları…"}
            data-testid="notes-input"
          />
        </Card>
      </div>
    </Shell>
  );
}
